import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Star, DollarSign, Plus, Clock, Lightbulb, Hotel, UtensilsCrossed, Car, Ticket, ExternalLink, Plane, Heart } from "lucide-react";
import { getDestination } from "../api";
import { useI18n, useL } from "../i18n";
import { useTrip } from "../TripContext";
import { useFavorites } from "../FavoritesContext";
import PriceGuideCard from "./PriceGuideCard";
import AdSlot from "./AdSlot";
import SocialShare from "./SocialShare";
import VideoHero from "./VideoHero";
import WeatherBadge from "./WeatherBadge";
import Reviews from "./Reviews";
import PhotoGallery from "./PhotoGallery";
import useSocialMeta from "../hooks/useSocialMeta";
import { bookingHotelUrl, agodaHotelUrl, skyscannerFlightUrl } from "../affiliates";

export default function DestinationDetail() {
  const { id } = useParams();
  const { lang, t } = useI18n();
  const L = useL();
  const { addStop } = useTrip();
  const { isFavorite, toggle } = useFavorites();
  const [dest, setDest] = useState(null);
  const Arrow = lang === "ar" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    getDestination(id).then(setDest);
  }, [id]);

  useSocialMeta({
    title: dest ? `${lang === "ar" ? dest.name_ar : dest.name_en} — Rahhal` : undefined,
    description: dest
      ? L({
          ar: `${dest.description_ar} · متوسط $${dest.avg_daily_cost_usd}/يوم`,
          en: `${dest.description_en} · Avg $${dest.avg_daily_cost_usd}/day`,
          fr: `${dest.description_en} · Moy. ${dest.avg_daily_cost_usd} $/jour`
        })
      : undefined,
    image: dest?.image_url,
  });

  if (!dest) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-[#78716C]">Loading…</div>;
  }

  const title = lang === "ar" ? dest.name_ar : dest.name_en;
  const city = lang === "ar" ? dest.city_ar : dest.city_en;
  const desc = lang === "ar" ? dest.description_ar : dest.description_en;

  return (
    <div className="bg-[#FAF7F2]" data-testid="destination-detail">
      <div className="relative h-[50vh] min-h-[380px] w-full overflow-hidden">
        <VideoHero videoUrl={dest.video_url} poster={dest.image_url} alt={title} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-transparent" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex flex-col justify-end pb-10 text-[#FAF7F2]">
          <Link to="/" className="inline-flex items-center gap-2 mb-4 text-sm opacity-90 hover:opacity-100">
            <Arrow className="h-4 w-4" />
            {L({ ar: "رجوع للاستكشاف", en: "Back to explore", fr: "Retour à l'exploration" })}
          </Link>
          <div className="text-xs uppercase tracking-widest opacity-90">{city}</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-4xl">{title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 items-center text-sm">
            <div className="flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-[#D97706] text-[#D97706]" />
              {dest.rating}
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {dest.best_time_ar}
            </div>
            <div className="flex items-center gap-1.5">
              <DollarSign className="h-4 w-4" />
              {dest.avg_daily_cost_usd} USD / {t("per_day")}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <section>
            <p className="text-lg text-[#1C1917] leading-relaxed">{desc}</p>
            <div className="mt-5">
              <SocialShare
                text={L({
                  ar: `اكتشف ${dest.city_ar} على رحّال — متوسط $${dest.avg_daily_cost_usd}/يوم`,
                  en: `Discover ${dest.city_en} on Rahhal — avg $${dest.avg_daily_cost_usd}/day`,
                  fr: `Découvrez ${dest.city_en} sur Rahhal — moy. ${dest.avg_daily_cost_usd} $/jour`
                })}
                testidPrefix="destination-share"
              />
            </div>
          </section>

          <section>
            <h2 className="font-display text-2xl text-[#1C1917] mb-5">{t("price_guide")}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <PriceGuideCard
                icon={Hotel}
                label={t("hotel")}
                budget={dest.prices.hotel_budget_usd}
                mid={dest.prices.hotel_mid_usd}
                luxury={dest.prices.hotel_luxury_usd}
              />
              <PriceGuideCard
                icon={UtensilsCrossed}
                label={t("meal")}
                budget={dest.prices.meal_budget_usd}
                mid={dest.prices.meal_mid_usd}
                luxury={dest.prices.meal_luxury_usd}
              />
              <PriceGuideCard
                icon={Car}
                label={t("transport")}
                single={dest.prices.transport_daily_usd}
              />
              <PriceGuideCard
                icon={Ticket}
                label={t("ticket")}
                single={dest.prices.ticket_usd}
              />
            </div>
          </section>

          {dest.tips_ar && dest.tips_ar.length > 0 && (
            <section>
              <h2 className="font-display text-2xl text-[#1C1917] mb-5 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-[#D97706]" />
                {t("tips")}
              </h2>
              <ul className="space-y-3">
                {dest.tips_ar.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-[#E6DED3]">
                    <div className="mt-1 h-6 w-6 shrink-0 rounded-full bg-[#FEF3C7] text-[#D97706] grid place-items-center font-semibold text-xs">
                      {i + 1}
                    </div>
                    <p className="text-sm text-[#1C1917] leading-relaxed">{tip}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="rounded-3xl overflow-hidden border border-[#E6DED3] bg-gradient-to-br from-[#F3EDE4] to-[#FAF7F2] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 relative">
            <div className="absolute top-3 end-3 text-[10px] uppercase tracking-widest bg-[#1C1917] text-[#FAF7F2] px-2 py-0.5 rounded-full">
              {t("sponsored")}
            </div>
            <div className="h-16 w-16 shrink-0 rounded-2xl bg-[#1C1917] text-[#FAF7F2] grid place-items-center">
              <Hotel className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl sm:text-2xl text-[#1C1917]">
                {L({
                  ar: `أفضل عروض فنادق ${dest.city_ar}`,
                  en: `Top hotel deals in ${dest.city_en}`,
                  fr: `Meilleures offres d'hôtels à ${dest.city_en}`
                })}
              </h3>
              <p className="mt-1 text-sm text-[#57534E]">
                {L({
                  ar: "قارن بين مئات الفنادق واحجز بأفضل سعر مضمون.",
                  en: "Compare hundreds of hotels and book at the best guaranteed price.",
                  fr: "Comparez des centaines d'hôtels et réservez au meilleur tarif garanti."
                })}
              </p>
            </div>
            <a
              href={bookingHotelUrl(dest.city_en)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              className="shrink-0 inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#C85A32] hover:bg-[#B24C28] text-[#FAF7F2] font-medium transition-colors"
            >
              {L({ ar: "شاهد العروض", en: "See deals", fr: "Voir les offres" })}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>

          <AdSlot slotId={process.env.REACT_APP_ADSENSE_SLOT_DETAIL_INLINE} layout="in-article" />

          {dest.gallery_urls && dest.gallery_urls.length > 0 && (
            <PhotoGallery images={dest.gallery_urls} alt={city} />
          )}

          <Reviews destId={dest.id} />
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-3xl bg-white border border-[#E6DED3] p-6 space-y-4">
            <WeatherBadge destId={dest.id} />

            <div>
              <div className="text-xs uppercase tracking-widest text-[#78716C]">{t("monthly_avg")}</div>
              <div className="font-display text-4xl text-[#1C1917] flex items-baseline gap-1 mt-1">
                <span className="text-lg text-[#C85A32]">$</span>
                {dest.avg_daily_cost_usd}
                <span className="text-sm text-[#78716C] font-normal ms-2">/ {t("per_day")}</span>
              </div>
            </div>

            <a
              href={bookingHotelUrl(dest.city_en)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="book-hotel-btn"
              className="w-full h-12 rounded-full bg-[#C85A32] hover:bg-[#B24C28] text-[#FAF7F2] flex items-center justify-center gap-2 transition-colors font-medium"
            >
              <Hotel className="h-4 w-4" />
              {L({ ar: "احجز فندقاً في " + dest.city_ar, en: "Book a hotel in " + dest.city_en, fr: "Réserver un hôtel à " + dest.city_en })}
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </a>

            <a
              href={skyscannerFlightUrl(dest.city_en)}
              target="_blank"
              rel="noopener noreferrer sponsored"
              data-testid="book-flight-btn"
              className="w-full h-11 rounded-full border border-[#E6DED3] hover:border-[#1C1917] text-[#1C1917] flex items-center justify-center gap-2 transition-colors text-sm font-medium"
            >
              <Plane className="h-3.5 w-3.5" />
              {L({ ar: "طيران إلى " + dest.city_ar, en: "Flights to " + dest.city_en, fr: "Vols vers " + dest.city_en })}
            </a>

            <button
              onClick={() => addStop(dest)}
              data-testid="detail-add-to-trip"
              className="w-full h-12 rounded-full bg-[#1C1917] hover:bg-[#57534E] text-[#FAF7F2] flex items-center justify-center gap-2 transition-colors font-medium"
            >
              <Plus className="h-4 w-4" />
              {t("add_to_trip")}
            </button>

            <button
              onClick={() => toggle(dest)}
              data-testid="detail-favorite-btn"
              className={`w-full h-11 rounded-full border-2 flex items-center justify-center gap-2 transition-colors text-sm font-medium ${
                isFavorite(dest.id)
                  ? "border-[#C85A32] bg-[#C85A32]/10 text-[#C85A32]"
                  : "border-[#E6DED3] text-[#1C1917] hover:border-[#1C1917]"
              }`}
            >
              <Heart className={`h-4 w-4 ${isFavorite(dest.id) ? "fill-[#C85A32]" : ""}`} />
              {isFavorite(dest.id)
                ? L({ ar: "في المفضلة", en: "In favorites", fr: "Dans les favoris" })
                : L({ ar: "أضف للمفضلة", en: "Add to favorites", fr: "Ajouter aux favoris" })}
            </button>

            <AdSlot slotId={process.env.REACT_APP_ADSENSE_SLOT_SIDEBAR} className="mt-2" layout="sidebar" />
          </div>
        </aside>
      </div>
    </div>
  );
}
